import React, { useState } from 'react';
import { Link } from 'react-router';
import { Button, Input, Card, Alert, Loader } from '@rfacil/shared/ui';
import { useForm, useNotificacionStore } from '@rfacil/shared/hooks';
import { validationUtils } from '@rfacil/shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock } from '@fortawesome/free-solid-svg-icons';
import { useLogin } from '../hooks/useAuth';
import { AuthLayout } from './AuthLayout';
import { useAuthStore } from '@rfacil/shared/services';
import {
  hasPendingFacturaLink,
  origenDeEnlacePendiente,
} from '../utils/pending-factura-link';
import { TEXTOS_ORIGEN_ENLACE } from '../../materialidad/app/utils/origenEnlace';

interface LoginFormProps {
  onLoginSuccess?: () => void;
}

/**
 * @component LoginForm
 * @description Un componente que proporciona una interfaz de usuario para iniciar sesión.
 * Incluye campos para nombre de usuario y contraseña, validación y manejo de errores.
 * Utiliza el gancho `useAuth` para manejar el proceso de inicio de sesión.
 * @param {object} props Las propiedades del componente.
 * @param {() => void} [props.onLoginSuccess] Una función de devolución de llamada que se llamará cuando el inicio de sesión sea exitoso.
 * @returns {React.ReactElement} El componente renderizado.
 */
export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const { showNotification } = useNotificacionStore();
  const [error, setError] = useState<string | null>(null);
  // Se lee una vez al montar y sin consumir el enlace: `AuthRedirect` lo retoma despues del login.
  const [hayFacturaPendiente] = useState(hasPendingFacturaLink);
  // El aviso nombra de donde viene el enlace (Power BI o correo); sin origen conocido es generico.
  const [origenFactura] = useState(origenDeEnlacePendiente);

  // Usar TanStack Query para el login
  const loginMutation = useLogin();
  const authStoreLoading = useAuthStore((state) => state.isLoading);
  const isLoading = loginMutation.isPending || authStoreLoading;

  const { values, errors, setValue, validateForm, isFormValid } = useForm(
    { user: '', password: '' },
    {
      user: (value: string) => (!value.trim() ? 'Usuario es requerido' : null),
      password: (value: string) =>
        !validationUtils.isValidPassword(value)
          ? 'Contraseña debe tener al menos 8 caracteres'
          : null,
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    try {
      await loginMutation.mutateAsync(values);
      onLoginSuccess?.();
    } catch (err) {
      console.error('Error al iniciar sesión:', err);
      // useAuthStore.error ya trae el mensaje procesado por el onError de useLogin
      // (extraído del backend); err.message es el rechazo crudo de axios y no debe usarse.
      const errorMessage =
        useAuthStore.getState().error ||
        (err instanceof Error ? err.message : 'Credenciales incorrectas');
      showNotification(errorMessage, 'error');
      setError(errorMessage);
    }
  };

  return (
    <>
      {isLoading && (
        <Loader
          message="Iniciando sesión..."
          subtitle="Validando tus credenciales"
        />
      )}
      {/* El marco (marca, fondo y aviso de privacidad) es el mismo de las pantallas de error del enlace de factura. */}
      <AuthLayout>
          <Card className="w-full max-w-md" padding="lg">
            <div className="text-center mb-8">
              {/* Logo naranja con estrella */}
              <div className="flex justify-center mb-4">
                <div className="w-40 h-20 rounded-lg flex items-center justify-center ">

                  <img src="/ICONO IMAGOTIPO.png" alt="Logo" className="w-full h-full object-contain" />
                </div>
              </div>

              <h1 className="text-gray-800 mb-2 text-3xl font-bold">
                Rfacil Materialidad
              </h1>
              <p className="text-gray-600 text-sm">
                Sistema de Gestión Empresarial
              </p>

            </div>

            {/* Llego desde un enlace de factura sin sesion: al entrar se abre esa factura. */}
            {hayFacturaPendiente && (
              <Alert variant="info" className="mb-5">
                {TEXTOS_ORIGEN_ENLACE[origenFactura].avisoLogin}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <Input
                  label="Usuario"
                  type="text"
                  value={values.user}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('user', e.target.value)
                  }
                  error={errors.user}
                  required
                  leftIcon={
                    <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                  }
                  placeholder="usuario@ejemplo.com"
                />
              </div>

              <div className="mb-6">
                <Input
                  label="Contraseña"
                  type="password"
                  value={values.password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('password', e.target.value)
                  }
                  error={errors.password}
                  required
                  leftIcon={
                    <FontAwesomeIcon icon={faLock} className="text-gray-400" />
                  }
                  placeholder="Ingresa tu contraseña"
                />
              </div>

              {error && (
                <Alert variant="error" className="mb-5">
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                loading={isLoading}
                fullWidth
                size="lg"
                disabled={!isFormValid()}
                className="bg-gradient-to-r from-host-500 to-host-600 hover:from-host-600 hover:to-host-700  text-text-secondary font-medium shadow-md"
              >
                {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>

              <p className="text-center mt-4">
                <Link
                  to="/olvide-contrasena"
                  className="text-sm text-host-600 hover:underline font-medium"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </p>
            </form>
          </Card>
      </AuthLayout>
    </>
  );
};

export default LoginForm;
